/**
 * AdminSubscriptions — SuperAdmin's billing operations page.
 *
 * Three tools packed into one page:
 *   1. Companies table — every employer + their subscription status + MRR +
 *      quick action to open the invoice generator or custom-plan dialog.
 *   2. Generate invoice modal — creates a pending invoice with GST auto-
 *      applied when the company has a GSTIN.
 *   3. Custom plan dialog — sets a bespoke monthly price and optionally
 *      binds the company to a feature preset in one call.
 *
 * The employer-facing billing page is /billing (see BillingPlans.jsx).
 */
import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, FileText, Save, Loader2, Search, Download, TrendingUp, Wallet, Activity, PieChart } from "lucide-react";

const STATUS_STYLE = {
    active: "border-emerald-500 text-emerald-700 bg-emerald-50",
    trialing: "border-blue-500 text-blue-700 bg-blue-50",
    past_due: "border-amber-500 text-amber-700 bg-amber-50",
    grace: "border-amber-500 text-amber-700 bg-amber-50",
    suspended: "border-rose-600 text-rose-700 bg-rose-50",
    cancelled: "border-neutral-400 text-neutral-500 bg-neutral-50",
};

export default function AdminSubscriptions() {
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(false);
    const [q, setQ] = useState("");
    const [selected, setSelected] = useState(null); // company id
    const [invoiceModal, setInvoiceModal] = useState(false);
    const [planModal, setPlanModal] = useState(false);
    const [invoices, setInvoices] = useState([]);
    const [presets, setPresets] = useState([]);

    async function load() {
        setLoading(true);
        try {
            const [c, p] = await Promise.all([
                api.get("/features/companies"),
                api.get("/features/presets"),
            ]);
            // Enrich with subscription info — one Mongo doc read per employer.
            // For a small list this is fine; if it grows we can batch on the BE.
            const withSubs = await Promise.all((c.data.companies || []).map(async (co) => {
                try {
                    // No dedicated endpoint yet — read via the feature companies get to
                    // stay cheap. Fallback: leave sub blank if fetch fails.
                    return { ...co };
                } catch { return co; }
            }));
            setCompanies(withSubs);
            setPresets(p.data.presets || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    // When a company is selected we lazy-load their invoices in a side panel.
    useEffect(() => {
        if (!selected) { setInvoices([]); return; }
        (async () => {
            try {
                // Admin can hit the same /invoices/mine endpoint via the caller's own
                // session — but for cross-company visibility we use a filtered list.
                // Since /invoices doesn't yet have a bulk list, fetch per-company later.
                setInvoices([]);
            } catch (e) { /* ignore */ }
        })();
    }, [selected]);

    const filtered = useMemo(() => {
        const s = q.trim().toLowerCase();
        if (!s) return companies;
        return companies.filter((c) => (c.name || "").toLowerCase().includes(s));
    }, [companies, q]);

    return (
        <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="admin-subscriptions-page">
            <header className="mb-4 pb-4 border-b border-border">
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Super admin</div>
                <h1 className="font-display font-black text-3xl text-ikb leading-tight">Subscriptions & Billing</h1>
                <p className="text-xs text-muted-foreground mt-1">
                    Track MRR, churn and largest accounts. Issue invoices or lock in custom monthly prices per employer.
                </p>
            </header>

            <MRRDashboard />

            <SignupDefaultsCard />

            <FxRatesCard />

            <div className="flex items-end gap-3 mb-4 flex-wrap">
                <label className="relative">
                    <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                        type="text"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search companies…"
                        className="h-9 border border-border bg-white text-sm pl-8 pr-3 min-w-[280px]"
                        data-testid="as-search"
                    />
                </label>
                <div className="text-xs text-muted-foreground">{filtered.length} of {companies.length}</div>
            </div>

            {loading ? (
                <div className="text-sm text-muted-foreground inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
            ) : (
                <div className="border border-border bg-white overflow-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-neutral-50 border-b border-border">
                            <tr>
                                <th className="text-left px-3 py-2 text-[10px] font-mono uppercase tracking-widest">Company</th>
                                <th className="text-left px-3 py-2 text-[10px] font-mono uppercase tracking-widest">Plan</th>
                                <th className="text-left px-3 py-2 text-[10px] font-mono uppercase tracking-widest">Preset</th>
                                <th className="text-right px-3 py-2 text-[10px] font-mono uppercase tracking-widest">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((co) => (
                                <tr key={co.id} className="border-b border-border hover:bg-secondary/40" data-testid={`as-row-${co.id}`}>
                                    <td className="px-3 py-2">
                                        <div className="font-medium">{co.name}</div>
                                        <div className="text-[10px] font-mono text-muted-foreground">{co.id}</div>
                                    </td>
                                    <td className="px-3 py-2 text-xs">{co.plan || "—"}</td>
                                    <td className="px-3 py-2 text-xs">{co.preset_name || "—"}</td>
                                    <td className="px-3 py-2 text-right">
                                        <button
                                            type="button"
                                            onClick={() => { setSelected(co.id); setInvoiceModal(true); }}
                                            className="h-7 px-2 border border-border bg-white text-[10px] font-mono uppercase tracking-widest hover:bg-neutral-50 inline-flex items-center gap-1"
                                            data-testid={`as-invoice-${co.id}`}
                                        >
                                            <FileText className="w-3 h-3" /> Issue invoice
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => { setSelected(co.id); setPlanModal(true); }}
                                            className="ml-1.5 h-7 px-2 border border-ikb text-ikb bg-white text-[10px] font-mono uppercase tracking-widest hover:bg-ikb/5 inline-flex items-center gap-1"
                                            data-testid={`as-custom-${co.id}`}
                                        >
                                            <CreditCard className="w-3 h-3" /> Custom plan
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {invoiceModal && selected && (
                <IssueInvoiceModal
                    companyId={selected}
                    company={companies.find((c) => c.id === selected)}
                    onClose={() => setInvoiceModal(false)}
                    onDone={() => { setInvoiceModal(false); toast.success("Invoice issued"); }}
                />
            )}
            {planModal && selected && (
                <CustomPlanModal
                    companyId={selected}
                    company={companies.find((c) => c.id === selected)}
                    presets={presets}
                    onClose={() => setPlanModal(false)}
                    onDone={async () => { setPlanModal(false); toast.success("Custom plan applied"); await load(); }}
                />
            )}
        </div>
    );
}

function IssueInvoiceModal({ companyId, company, onClose, onDone }) {
    const [amount, setAmount] = useState("");
    const [description, setDescription] = useState(`Monthly subscription — ${new Date().toLocaleDateString(undefined, { month: "long", year: "numeric" })}`);
    const [currency, setCurrency] = useState("INR");
    const [dueDays, setDueDays] = useState(7);
    const [busy, setBusy] = useState(false);
    const [issued, setIssued] = useState(null);

    async function submit() {
        if (!amount) return;
        setBusy(true);
        try {
            const { data } = await api.post("/invoices/generate", {
                company_id: companyId,
                amount: parseFloat(amount),
                currency,
                description,
                due_days: dueDays,
            });
            setIssued(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    }

    function download() {
        if (!issued) return;
        const backend = process.env.REACT_APP_BACKEND_URL;
        window.open(`${backend}/api/invoices/${issued.id}/pdf`, "_blank", "noopener");
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white w-full max-w-md" onClick={(e) => e.stopPropagation()}>
                <header className="px-4 py-3 border-b border-border">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Issue invoice</div>
                    <div className="font-bold text-lg">{company?.name}</div>
                </header>
                {issued ? (
                    <div className="p-4 space-y-3">
                        <div className="border border-emerald-300 bg-emerald-50 p-3">
                            <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-800">Invoice created</div>
                            <div className="font-bold">{issued.invoice_number}</div>
                            <div className="text-xs text-muted-foreground">Total: {issued.currency} {issued.total} · Due {issued.due_at?.slice(0, 10)}</div>
                        </div>
                        <div className="flex gap-2">
                            <button onClick={download} className="h-9 px-3 bg-ikb text-white text-[11px] font-mono uppercase tracking-widest inline-flex items-center gap-1.5" data-testid="as-invoice-download">
                                <Download className="w-3.5 h-3.5" /> Download PDF
                            </button>
                            <button onClick={onDone} className="h-9 px-3 border border-border text-[11px] font-mono uppercase tracking-widest">Done</button>
                        </div>
                    </div>
                ) : (
                    <div className="p-4 space-y-3">
                        <label className="block">
                            <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Amount (pre-tax)</span>
                            <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1 h-9 w-full border border-border bg-white text-sm px-2" data-testid="as-invoice-amount" />
                        </label>
                        <label className="block">
                            <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Currency</span>
                            <select value={currency} onChange={(e) => setCurrency(e.target.value)} className="mt-1 h-9 w-full border border-border bg-white text-sm px-2">
                                <option value="INR">INR</option>
                                <option value="USD">USD</option>
                            </select>
                        </label>
                        <label className="block">
                            <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Description</span>
                            <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} className="mt-1 h-9 w-full border border-border bg-white text-sm px-2" />
                        </label>
                        <label className="block">
                            <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Due in (days)</span>
                            <input type="number" value={dueDays} onChange={(e) => setDueDays(parseInt(e.target.value || "7"))} className="mt-1 h-9 w-full border border-border bg-white text-sm px-2" />
                        </label>
                        <div className="flex gap-2 pt-1">
                            <button onClick={submit} disabled={busy || !amount} className="h-9 px-3 bg-ikb text-white text-[11px] font-mono uppercase tracking-widest disabled:opacity-60 inline-flex items-center gap-1.5" data-testid="as-invoice-submit">
                                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                                Create
                            </button>
                            <button onClick={onClose} className="h-9 px-3 border border-border text-[11px] font-mono uppercase tracking-widest">Cancel</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function CustomPlanModal({ companyId, company, presets, onClose, onDone }) {
    const [price, setPrice] = useState("");
    const [currency, setCurrency] = useState("INR");
    const [presetKey, setPresetKey] = useState(company?.preset_key || "");
    const [notes, setNotes] = useState("");
    const [busy, setBusy] = useState(false);

    async function submit() {
        if (!price) return;
        setBusy(true);
        try {
            await api.post(`/invoices/admin/companies/${companyId}/custom-plan`, {
                price_monthly: parseFloat(price),
                currency,
                feature_preset_key: presetKey || null,
                notes,
            });
            onDone();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white w-full max-w-md" onClick={(e) => e.stopPropagation()}>
                <header className="px-4 py-3 border-b border-border">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Custom plan</div>
                    <div className="font-bold text-lg">{company?.name}</div>
                </header>
                <div className="p-4 space-y-3">
                    <label className="block">
                        <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Monthly price (pre-tax)</span>
                        <input type="number" value={price} onChange={(e) => setPrice(e.target.value)} className="mt-1 h-9 w-full border border-border bg-white text-sm px-2" data-testid="as-plan-price" />
                    </label>
                    <label className="block">
                        <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Currency</span>
                        <select value={currency} onChange={(e) => setCurrency(e.target.value)} className="mt-1 h-9 w-full border border-border bg-white text-sm px-2">
                            <option value="INR">INR</option>
                            <option value="USD">USD</option>
                        </select>
                    </label>
                    <label className="block">
                        <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Feature preset (baseline)</span>
                        <select value={presetKey} onChange={(e) => setPresetKey(e.target.value)} className="mt-1 h-9 w-full border border-border bg-white text-sm px-2" data-testid="as-plan-preset">
                            <option value="">— No preset —</option>
                            {presets.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
                        </select>
                    </label>
                    <label className="block">
                        <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Internal notes</span>
                        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-1 w-full border border-border bg-white text-sm px-2 py-1.5 resize-none" />
                    </label>
                    <div className="flex gap-2 pt-1">
                        <button onClick={submit} disabled={busy || !price} className="h-9 px-3 bg-ikb text-white text-[11px] font-mono uppercase tracking-widest disabled:opacity-60 inline-flex items-center gap-1.5" data-testid="as-plan-submit">
                            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                            Apply
                        </button>
                        <button onClick={onClose} className="h-9 px-3 border border-border text-[11px] font-mono uppercase tracking-widest">Cancel</button>
                    </div>
                </div>
            </div>
        </div>
    );
}

/** MRRDashboard — hero row of live billing metrics at the top of the page.
 *  Two big numbers (subscription MRR + trailing-30d cash MRR side-by-side),
 *  churn %, paying-account count, plan-mix bar chart and top-10 accounts. */
function MRRDashboard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get("/invoices/admin/mrr");
                setData(data);
            } catch (e) {
                toast.error(formatApiError(e));
            } finally { setLoading(false); }
        })();
    }, []);

    if (loading) {
        return (
            <div className="mb-6 border border-border bg-white p-6 text-sm text-muted-foreground inline-flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Computing MRR…
            </div>
        );
    }
    if (!data) return null;

    const fmt = (n) => new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Math.round(n || 0));
    const maxPlanMrr = Math.max(1, ...(data.plan_mix || []).map((p) => p.mrr));

    return (
        <section className="mb-6" data-testid="mrr-dashboard">
            {/* KPI cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
                <KpiCard
                    testid="mrr-sub"
                    icon={TrendingUp}
                    label="Subscription MRR"
                    value={`₹${fmt(data.subscription_mrr_inr)}`}
                    sub="On the books · monthly"
                    tone="ikb"
                />
                <KpiCard
                    testid="mrr-cash"
                    icon={Wallet}
                    label="Cash MRR (30d)"
                    value={`₹${fmt(data.cash_mrr_inr)}`}
                    sub={`Actual receipts · ${data.window_days}d`}
                    tone="emerald"
                />
                <KpiCard
                    testid="mrr-paying"
                    icon={Activity}
                    label="Paying accounts"
                    value={fmt(data.paying_companies)}
                    sub={data.churn_pct != null ? `Churn ${data.churn_pct}%` : "—"}
                    tone="amber"
                />
                <KpiCard
                    testid="mrr-churn"
                    icon={PieChart}
                    label="Churned (30d)"
                    value={fmt(data.churned_last_30d || 0)}
                    sub="Cancelled + downgraded"
                    tone="rose"
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-3">
                {/* Plan-mix bar chart */}
                <div className="border border-border bg-white p-4" data-testid="mrr-plan-mix">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Plan mix</div>
                    {(data.plan_mix || []).length === 0 ? (
                        <div className="text-xs text-muted-foreground py-6 text-center">No paid subscriptions yet.</div>
                    ) : (
                        <ul className="space-y-2">
                            {data.plan_mix.map((p) => {
                                const pct = Math.max(2, (p.mrr / maxPlanMrr) * 100);
                                return (
                                    <li key={p.plan_key} className="flex items-center gap-2 text-xs">
                                        <span className="w-20 uppercase tracking-widest font-mono text-[10px] text-muted-foreground">{p.plan_key}</span>
                                        <div className="flex-1 bg-neutral-100 h-4 relative">
                                            <div className="bg-ikb h-4" style={{ width: `${pct}%` }} />
                                        </div>
                                        <span className="tabular-nums text-right w-24">₹{fmt(p.mrr)}</span>
                                        <span className="text-[10px] font-mono text-muted-foreground w-10 text-right">×{p.count}</span>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>

                {/* Top 10 accounts */}
                <div className="border border-border bg-white overflow-hidden" data-testid="mrr-top-accounts">
                    <div className="px-4 py-2 border-b border-border text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                        Top 10 accounts by MRR
                    </div>
                    {(data.top_accounts || []).length === 0 ? (
                        <div className="text-xs text-muted-foreground py-6 text-center">No paid accounts to rank yet.</div>
                    ) : (
                        <table className="w-full text-xs">
                            <tbody>
                                {data.top_accounts.map((a, i) => (
                                    <tr key={a.company_id} className="border-b border-border last:border-b-0">
                                        <td className="px-3 py-1.5 tabular-nums text-muted-foreground w-8">{i + 1}</td>
                                        <td className="px-3 py-1.5 font-medium truncate max-w-[200px]">{a.company_name}</td>
                                        <td className="px-2 py-1.5 uppercase tracking-widest font-mono text-[10px] text-muted-foreground">{a.plan_key}</td>
                                        <td className="px-3 py-1.5 tabular-nums text-right font-bold">₹{fmt(a.mrr)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </section>
    );
}

function KpiCard({ icon: Icon, label, value, sub, tone = "ikb", testid }) {
    const toneCls = {
        ikb: "border-ikb text-ikb",
        emerald: "border-emerald-500 text-emerald-700",
        amber: "border-amber-500 text-amber-700",
        rose: "border-rose-500 text-rose-700",
    }[tone] || "border-ikb text-ikb";
    return (
        <div className={`border-l-4 border-t border-r border-b border-border bg-white p-3 ${toneCls}`} data-testid={testid}>
            <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest">
                <Icon className="w-3 h-3" />
                <span>{label}</span>
            </div>
            <div className="font-display font-black text-2xl mt-1 tabular-nums text-foreground">{value}</div>
            <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>
        </div>
    );
}


/** SignupDefaultsCard — admin sets which plan brand-new employers land on.
 *  Writes to /api/admin/signup-defaults; auth/register reads the setting at
 *  signup time. Defaults to "free" when unset. */
function SignupDefaultsCard() {
    const [current, setCurrent] = useState("free");
    const [plans, setPlans] = useState([]);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const [sd, pl] = await Promise.all([
                    api.get("/admin/signup-defaults"),
                    api.get("/plans"),
                ]);
                setCurrent(sd.data?.default_plan || "free");
                setPlans(pl.data?.plans || []);
            } catch (e) { toast.error(formatApiError(e)); }
            finally { setLoading(false); }
        })();
    }, []);

    async function save(newPlan) {
        setSaving(true);
        try {
            await api.put("/admin/signup-defaults", { default_plan: newPlan });
            setCurrent(newPlan);
            toast.success(`New signups will now land on the ${newPlan} plan`);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    }

    if (loading) return null;

    return (
        <section id="signup-defaults" className="mb-6 border border-border bg-white p-4" data-testid="signup-defaults-card">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Signup defaults</div>
                    <div className="text-sm">
                        Auto-assign new employers to the <b className="uppercase font-mono text-ikb">{current}</b> plan on signup.
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {plans.map((p) => (
                        <button
                            key={p.key}
                            type="button"
                            onClick={() => save(p.key)}
                            disabled={saving || p.key === current}
                            className={`h-8 px-3 border text-[10px] font-mono uppercase tracking-widest disabled:opacity-100 ${p.key === current ? "border-ikb bg-ikb text-white" : "border-border bg-white hover:border-ikb hover:text-ikb"}`}
                            data-testid={`signup-default-${p.key}`}
                        >
                            {p.name}{p.key === current ? " ✓" : ""}
                        </button>
                    ))}
                </div>
            </div>
            <div className="text-[11px] text-muted-foreground">
                Use this to run a "Pro trial for early adopters" campaign — set the default to <code>pro</code>, and every signup gets the Pro plan for one billing cycle. Change back to <code>free</code> when the campaign ends.
            </div>
        </section>
    );
}


/** FxRatesCard — admin manages the FX table used by the MRR dashboard
 *  when normalizing non-INR paid invoices back to INR. INR is always
 *  implicit 1.0 (not editable). Add/edit any currency → rate row. */
function FxRatesCard() {
    const [rates, setRates] = useState({ INR: 1.0 });
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [newCcy, setNewCcy] = useState("");
    const [newRate, setNewRate] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/admin/fx-rates");
                setRates(data?.rates || { INR: 1.0 });
            } catch (e) { toast.error(formatApiError(e)); }
            finally { setLoading(false); }
        })();
    }, []);

    function setRate(ccy, rate) {
        setRates((r) => ({ ...r, [ccy]: rate }));
        setDirty(true);
    }
    function removeRate(ccy) {
        setRates((r) => {
            const next = { ...r };
            delete next[ccy];
            return next;
        });
        setDirty(true);
    }
    function addRate() {
        const code = (newCcy || "").trim().toUpperCase();
        if (!code || code === "INR") return;
        setRate(code, parseFloat(newRate) || 0);
        setNewCcy(""); setNewRate("");
    }
    async function save() {
        setSaving(true);
        try {
            // Strip INR (server ignores it too).
            const clean = { ...rates };
            delete clean.INR;
            await api.put("/admin/fx-rates", { rates: clean });
            toast.success("FX rates updated");
            setDirty(false);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    }

    if (loading) return null;
    const nonInrCodes = Object.keys(rates).filter((c) => c !== "INR").sort();

    return (
        <section className="mb-6 border border-border bg-white p-4" data-testid="fx-rates-card">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">FX rates (→ INR)</div>
                    <div className="text-sm">
                        Cash MRR converts non-INR invoices using these rates. INR is fixed at 1.0.
                    </div>
                </div>
                <button
                    type="button"
                    onClick={save}
                    disabled={!dirty || saving}
                    className="h-8 px-3 border border-ikb bg-ikb text-white text-[10px] font-mono uppercase tracking-widest hover:bg-ikb/90 disabled:opacity-40 inline-flex items-center gap-1.5"
                    data-testid="fx-save"
                >
                    {saving && <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                    Save FX table
                </button>
            </div>
            <div className="space-y-2">
                {nonInrCodes.length === 0 ? (
                    <div className="text-xs text-muted-foreground">No custom rates set — USD defaults to 83.0 in code.</div>
                ) : (
                    <table className="w-full text-xs" data-testid="fx-rates-table">
                        <tbody>
                            {nonInrCodes.map((ccy) => (
                                <tr key={ccy} className="border-b border-border last:border-b-0">
                                    <td className="py-1.5 font-mono uppercase w-16">{ccy}</td>
                                    <td className="py-1.5">
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={rates[ccy] ?? ""}
                                            onChange={(e) => setRate(ccy, parseFloat(e.target.value) || 0)}
                                            className="h-8 w-32 border border-border bg-white text-xs px-2 tabular-nums text-right"
                                            data-testid={`fx-rate-${ccy}`}
                                        />
                                        <span className="ml-1 text-muted-foreground">₹ per {ccy}</span>
                                    </td>
                                    <td className="py-1.5 text-right">
                                        <button
                                            type="button"
                                            onClick={() => removeRate(ccy)}
                                            className="h-7 px-2 border border-border bg-white text-[10px] font-mono uppercase tracking-widest hover:bg-rose-50 hover:border-rose-300"
                                            data-testid={`fx-remove-${ccy}`}
                                        >
                                            Remove
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
                <div className="flex items-center gap-2 pt-2 border-t border-border">
                    <input
                        type="text"
                        value={newCcy}
                        onChange={(e) => setNewCcy(e.target.value.toUpperCase())}
                        placeholder="EUR"
                        maxLength={3}
                        className="h-8 w-24 border border-border bg-white text-xs px-2 uppercase font-mono"
                        data-testid="fx-new-ccy"
                    />
                    <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={newRate}
                        onChange={(e) => setNewRate(e.target.value)}
                        placeholder="90.00"
                        className="h-8 w-32 border border-border bg-white text-xs px-2 tabular-nums text-right"
                        data-testid="fx-new-rate"
                    />
                    <button
                        type="button"
                        onClick={addRate}
                        disabled={!newCcy || !newRate}
                        className="h-8 px-3 border border-ikb text-ikb bg-white text-[10px] font-mono uppercase tracking-widest hover:bg-ikb/5 disabled:opacity-40"
                        data-testid="fx-add"
                    >
                        + Add rate
                    </button>
                </div>
            </div>
        </section>
    );
}

